import premiumShopsConfig from './config/premium-shops.json';
import { isLocalSearchRuntime } from './searchSubmit';

/**
 * @typedef {{
 *   rootDomain: string,
 *   name: string,
 *   faviconUrl?: string,
 *   shippingMessage?: string,
 *   tagline?: string,
 *   categorySlug?: string,
 * }} PremiumShop
 */

const HOMEPAGE_LOAD_COUNT_KEY = 'shopsmt_homepage_load_count';
const CATEGORY_LOAD_COUNT_PREFIX = 'shopsmt_category_load_count:';
const SHOP_COUNT_CACHE_KEY = 'shopsmt_browse_shop_count';

/** @type {PremiumShop[]} */
const premiumShops = Array.isArray(premiumShopsConfig?.shops)
  ? premiumShopsConfig.shops.filter(
      (shop) =>
        shop &&
        typeof shop.rootDomain === 'string' &&
        shop.rootDomain.trim() !== '' &&
        typeof shop.name === 'string' &&
        shop.name.trim() !== '',
    )
  : [];

/**
 * @returns {PremiumShop[]}
 */
export function listPremiumShops() {
  return premiumShops.slice();
}

/**
 * @param {string} hostname
 * @param {string} rootDomain
 * @returns {boolean}
 */
function hostnameMatchesRootDomain(hostname, rootDomain) {
  const host = hostname.replace(/^www\./i, '').toLowerCase();
  const root = rootDomain.replace(/^www\./i, '').toLowerCase().trim();
  if (host === '' || root === '') {
    return false;
  }
  return host === root || host.endsWith(`.${root}`);
}

/**
 * @param {string|null|undefined} url
 * @returns {string}
 */
function hostnameFromProductUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed === '') {
    return '';
  }
  try {
    return new URL(trimmed).hostname;
  } catch {
    return '';
  }
}

/**
 * Resolve premium shop config for a product (by product URL domain, then merchant name).
 *
 * @param {{ url?: string|null, merchant?: string|null }} product
 * @returns {PremiumShop|null}
 */
export function getPremiumShopForProduct(product) {
  if (!product || premiumShops.length === 0) {
    return null;
  }

  const hostname = hostnameFromProductUrl(product.url);
  if (hostname !== '') {
    const byDomain = premiumShops.find((shop) =>
      hostnameMatchesRootDomain(hostname, shop.rootDomain),
    );
    if (byDomain) {
      return byDomain;
    }
  }

  const merchant =
    typeof product.merchant === 'string' ? product.merchant.trim().toLowerCase() : '';
  if (merchant === '') {
    return null;
  }

  return (
    premiumShops.find(
      (shop) => typeof shop.name === 'string' && shop.name.trim().toLowerCase() === merchant,
    ) ?? null
  );
}

/**
 * @param {PremiumShop} shop
 * @returns {string}
 */
export function premiumShopWebsiteHost(shop) {
  if (!shop || typeof shop.rootDomain !== 'string') {
    return '';
  }
  return shop.rootDomain.replace(/^www\./i, '').toLowerCase().trim();
}

/**
 * @param {PremiumShop} shop
 * @returns {string}
 */
export function premiumShopSearchPath(shop) {
  const host = premiumShopWebsiteHost(shop);
  if (host === '') {
    return '/search';
  }
  return `/search?website=${encodeURIComponent(host)}`;
}

/**
 * @returns {number}
 */
function readHomepageLoadCount() {
  if (typeof window === 'undefined') {
    return 0;
  }
  try {
    const raw = window.localStorage.getItem(HOMEPAGE_LOAD_COUNT_KEY);
    const n = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * @returns {number} New load count after increment
 */
function incrementHomepageLoadCount() {
  const next = readHomepageLoadCount() + 1;
  if (typeof window === 'undefined') {
    return next;
  }
  try {
    window.localStorage.setItem(HOMEPAGE_LOAD_COUNT_KEY, String(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

/**
 * @returns {number|null}
 */
function readCachedBrowseShopCount() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SHOP_COUNT_CACHE_KEY);
    const n = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * @param {number} count
 */
function writeCachedBrowseShopCount(count) {
  if (typeof window === 'undefined' || !Number.isFinite(count) || count <= 0) {
    return;
  }
  try {
    window.localStorage.setItem(SHOP_COUNT_CACHE_KEY, String(Math.floor(count)));
  } catch {
    /* ignore */
  }
}

/**
 * @returns {Promise<number>}
 */
async function fetchBrowseShopCount() {
  const cached = readCachedBrowseShopCount();
  try {
    const res = await fetch('/api/browse-websites.php');
    if (!res.ok) {
      return cached ?? Math.max(premiumShops.length, 1);
    }
    const data = await res.json();
    const websites = Array.isArray(data?.websites) ? data.websites : [];
    const count = websites.length > 0 ? websites.length : Math.max(premiumShops.length, 1);
    writeCachedBrowseShopCount(count);
    return count;
  } catch {
    return cached ?? Math.max(premiumShops.length, 1);
  }
}

/**
 * @param {PremiumShop[]} shops
 * @param {number} rotationIndex
 * @returns {PremiumShop}
 */
function pickPremiumShopByRotation(shops, rotationIndex) {
  const idx = ((rotationIndex % shops.length) + shops.length) % shops.length;
  return shops[idx];
}

/** Memoize one decision per page lifetime (avoids Strict Mode double-increment). */
let featuredPremiumHomepageDecisionPromise = null;

/**
 * Decide which premium shop (if any) to feature on this homepage load.
 * Local: always feature a premium shop.
 * Production: feature on every Xth load, where X is the total browse shop count.
 *
 * @returns {Promise<PremiumShop|null>}
 */
export async function resolveFeaturedPremiumShopForHomepage() {
  if (featuredPremiumHomepageDecisionPromise) {
    return featuredPremiumHomepageDecisionPromise;
  }

  featuredPremiumHomepageDecisionPromise = (async () => {
    if (premiumShops.length === 0) {
      return null;
    }

    if (isLocalSearchRuntime()) {
      const loadCount = incrementHomepageLoadCount();
      return pickPremiumShopByRotation(premiumShops, loadCount - 1);
    }

    const [totalShops, loadCount] = await Promise.all([
      fetchBrowseShopCount(),
      Promise.resolve(incrementHomepageLoadCount()),
    ]);

    const interval = Math.max(1, totalShops);
    if (loadCount % interval !== 0) {
      return null;
    }

    return pickPremiumShopByRotation(
      premiumShops,
      Math.floor(loadCount / interval) - 1,
    );
  })();

  return featuredPremiumHomepageDecisionPromise;
}

/**
 * @param {string|null|undefined} slug
 * @returns {string}
 */
function normalizeCategorySlug(slug) {
  return typeof slug === 'string' ? slug.trim().toLowerCase() : '';
}

/**
 * Premium shops configured for a category hub slug.
 *
 * @param {string|null|undefined} categorySlug
 * @returns {PremiumShop[]}
 */
export function listPremiumShopsForCategory(categorySlug) {
  const wanted = normalizeCategorySlug(categorySlug);
  if (wanted === '') {
    return [];
  }
  return premiumShops.filter(
    (shop) => normalizeCategorySlug(shop.categorySlug) === wanted,
  );
}

/**
 * @param {string} categorySlug
 * @returns {number}
 */
function readCategoryLoadCount(categorySlug) {
  if (typeof window === 'undefined') {
    return 0;
  }
  try {
    const raw = window.localStorage.getItem(
      `${CATEGORY_LOAD_COUNT_PREFIX}${normalizeCategorySlug(categorySlug)}`,
    );
    const n = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * @param {string} categorySlug
 * @returns {number}
 */
function incrementCategoryLoadCount(categorySlug) {
  const key = normalizeCategorySlug(categorySlug);
  const next = readCategoryLoadCount(key) + 1;
  if (typeof window === 'undefined' || key === '') {
    return next;
  }
  try {
    window.localStorage.setItem(`${CATEGORY_LOAD_COUNT_PREFIX}${key}`, String(next));
  } catch {
    /* ignore */
  }
  return next;
}

/** @type {Map<string, Promise<PremiumShop|null>>} */
const featuredPremiumCategoryDecisionPromises = new Map();

/**
 * Decide which premium shop (if any) to feature on a category hub page.
 * Local: always feature when a premium shop is mapped to this category.
 * Production: feature on every Xth visit to this category, where X is total shops.
 *
 * @param {string|null|undefined} categorySlug
 * @returns {Promise<PremiumShop|null>}
 */
export async function resolveFeaturedPremiumShopForCategory(categorySlug) {
  const key = normalizeCategorySlug(categorySlug);
  if (key === '') {
    return null;
  }

  const existing = featuredPremiumCategoryDecisionPromises.get(key);
  if (existing) {
    return existing;
  }

  const decisionPromise = (async () => {
    const matching = listPremiumShopsForCategory(key);
    if (matching.length === 0) {
      return null;
    }

    if (isLocalSearchRuntime()) {
      const loadCount = incrementCategoryLoadCount(key);
      return pickPremiumShopByRotation(matching, loadCount - 1);
    }

    const [totalShops, loadCount] = await Promise.all([
      fetchBrowseShopCount(),
      Promise.resolve(incrementCategoryLoadCount(key)),
    ]);

    const interval = Math.max(1, totalShops);
    if (loadCount % interval !== 0) {
      return null;
    }

    return pickPremiumShopByRotation(
      matching,
      Math.floor(loadCount / interval) - 1,
    );
  })();

  featuredPremiumCategoryDecisionPromises.set(key, decisionPromise);
  return decisionPromise;
}
